import {
  useEffect, useMemo, useRef, useState,
} from 'react'

// Same check react-device-detect performs; inlined because this repo does not
// depend on it. iPadOS 13+ reports itself as a Mac, hence the second clause.
export const isIosDevice = typeof navigator !== 'undefined' && (
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
)

const isIOS = isIosDevice

// iOS does not shrink the page when its keyboard opens: lists must open upwards
// to stay clear of it, and dismissing it blurs the field, which would otherwise
// wipe the typed filter text. Both false on Android and desktop.
//
// Frozen so the popper props keep a stable identity and do not remount the
// popper on every render. Placement only sticks with flip off: flip measures
// the layout viewport, which knows nothing about the keyboard. preventOverflow
// stays on.
const POPPER_PROPS = Object.freeze({
  placement: 'top-start',
  popperOptions: {
    modifiers: [{ name: 'flip', enabled: false }],
  },
})

// An upwards list must be capped to the room above the field, or it runs off
// the top of the screen. iOS keeps scrolling the field for a few hundred ms
// after the list opens, so measure repeatedly rather than once.
const GAP = 8
const MIN_HEIGHT = 120
const INITIAL_METRICS = { upwards: false, maxHeight: null, liftAboveKeyboard: 0 }
// Keyboard animation, then the scroll iOS does after it.
const SETTLE_MS = [120, 300, 600]

/** Bottom edge of the app bar in viewport coords, or 0 once scrolled past. */
const getTopInset = () => {
  const bar = document.querySelector('header.MuiAppBar-root')
  if (!bar) return 0
  return Math.max(0, Math.round(bar.getBoundingClientRect().bottom))
}

/** Off iOS every flag is false and maxHeight null, leaving behaviour unchanged. */
const useIosDropdownPlacement = (anchorRef, open) => {
  const [{ upwards, maxHeight, liftAboveKeyboard }, setMetrics] = useState(INITIAL_METRICS)
  const wasOpen = useRef(false)

  // Selecting an option closes the list but leaves the field focused, so the
  // keyboard stays up over a form that has just shrunk back. Drop focus when
  // the list closes; the typed text survives because clearOnBlur is off here.
  useEffect(() => {
    if (isIOS && wasOpen.current && !open) {
      document.activeElement?.blur?.()
    }
    wasOpen.current = open
  }, [open])

  useEffect(() => {
    if (!open || !isIOS) {
      setMetrics(INITIAL_METRICS)
      return undefined
    }

    let frame = null
    const timers = []

    const measure = () => {
      const rect = anchorRef.current?.getBoundingClientRect()
      if (!rect) return
      // `visibleBottom` is the keyboard's top edge when one is up, the screen
      // bottom otherwise - so this compares real, on-screen room.
      const visibleBottom = window.visualViewport?.height ?? window.innerHeight
      const spaceAbove = Math.round(rect.top - getTopInset() - GAP)
      const spaceBelow = Math.round(visibleBottom - rect.bottom - GAP)

      setMetrics((current) => {
        // Only go up when there is genuinely no room below. Opening upwards by
        // default sent arrow-opened lists (which raise no keyboard) off the top
        // of the screen. Latched, so the keyboard appearing can force it but
        // nothing flips it back mid-open, which would make the list jump.
        const goUp = current.upwards ||
          (spaceBelow < MIN_HEIGHT && spaceAbove > spaceBelow)
        // An upwards list sits with its bottom on the field. If the field is
        // itself inside the keyboard area, that bottom - and the Load More row
        // that lives there - is hidden, so lift the popper clear of it.
        const lift = goUp ? Math.max(0, Math.round(rect.top - visibleBottom + GAP)) : 0
        const space = goUp ? spaceAbove - lift : spaceBelow
        const next = {
          upwards: goUp,
          maxHeight: Math.max(MIN_HEIGHT, space),
          liftAboveKeyboard: lift,
        }

        // Only emit on a real change: a needless state update re-renders the
        // field, which remounts an inline PaperComponent and resets the list.
        const unchanged = current.upwards === next.upwards &&
          current.maxHeight === next.maxHeight &&
          current.liftAboveKeyboard === next.liftAboveKeyboard

        return unchanged ? current : next
      })
    }

    // Scrolling the options list must not trigger a re-measure: the field has
    // not moved, and the resulting render can reset the list back to the top.
    const isScrollInsidePopup = (event) => Boolean(
      event?.target?.closest?.('.MuiAutocomplete-popper, .MuiPopover-root, .MuiMenu-root'),
    )

    const update = (event) => {
      if (isScrollInsidePopup(event)) return
      if (frame) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }

    update()
    SETTLE_MS.forEach((ms) => timers.push(setTimeout(update, ms)))

    // Deliberately NOT listening to scroll. A capture-phase scroll listener
    // also fires when the option list itself is scrolled: that re-measures, the
    // anchor has moved, the height changes, and the re-render remounts the
    // inline PaperComponent - collapsing the list mid-scroll. The settle ticks
    // above plus a keyboard resize are enough.
    window.addEventListener('resize', update)
    window.visualViewport?.addEventListener('resize', update)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      timers.forEach(clearTimeout)
      window.removeEventListener('resize', update)
      window.visualViewport?.removeEventListener('resize', update)
    }
  }, [open, anchorRef])

  const popperProps = useMemo(() => (liftAboveKeyboard === 0 ? POPPER_PROPS : {
    ...POPPER_PROPS,
    popperOptions: {
      modifiers: [
        ...POPPER_PROPS.popperOptions.modifiers,
        { name: 'offset', options: { offset: [0, liftAboveKeyboard] } },
      ],
    },
  }), [liftAboveKeyboard])

  return {
    upwards,
    maxHeight,
    popperProps,
    keepTypedText: isIOS,
  }
}

export default useIosDropdownPlacement
